import React, { useEffect, useState } from "react";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CardSkeleton, TableSkeleton } from "../../../components/skeleton/ListSkeleton";
import {
  DailyReportDetail,
  DailyReportPayload,
  MonthlyReportDetail,
  MonthlyReportPayload,
  ReportDoctor,
  ReportKind,
  ReportKpiKey,
  ReportListItem,
  getDailyReport,
  getMonthlyReport,
  getReports,
} from "../analyticsApi";
import {
  formatChange,
  formatDecimal,
  formatNumber,
  formatPct,
  formatRs,
  formatStars,
  formatUnit,
  healthBand,
  kpiRowTone,
  longDate,
  reportIsNew,
  reportTitle,
  scoreWidth,
  sortReportsNewest,
  sourceLabel,
} from "../analyticsUtils";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import EmptyState from "../components/EmptyState";
import { CARD, CARD_PAD, Caption, EmptyRow, SectionTitle, TD, TD_NUM, TH, TH_NUM, TR, TableCard } from "../components/ui";
import "./analyticsPrint.css";

const KPI_ORDER: ReportKpiKey[] = ["billed", "collected", "collectionRate", "averageBill", "discount", "newPatients", "appointments", "noShowRate", "planCompletionRate"];

const CHANGE_TONE = {
  good: "text-emerald-600 dark:text-emerald-400",
  bad: "text-rose-600 dark:text-rose-400",
  neutral: "text-slate-400 dark:text-slate-500",
};

const BAND_TEXT = {
  green: "text-emerald-600 dark:text-emerald-400",
  amber: "text-amber-600 dark:text-amber-400",
  red: "text-rose-600 dark:text-rose-400",
};

const HEAD = "border-b border-slate-200 dark:border-slate-600";
const BODY = "divide-y divide-slate-100 dark:divide-slate-600";
const TOGGLE_BTN =
  "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30";
const TOGGLE_ON = "bg-primary/10 text-primary border border-primary/30 dark:text-primaryLight dark:bg-primary/20";
const TOGGLE_OFF = "border border-transparent text-slate-500 hover:text-primary hover:bg-primary/5 dark:text-slate-400 dark:hover:text-primaryLight";

function generatedLabel(generatedAt: string | null | undefined): string {
  if (!generatedAt) return "—";
  const d = new Date(generatedAt);
  return Number.isNaN(d.getTime()) ? "—" : longDate(d.toISOString().slice(0, 10));
}

function scoreText(score: number | null | undefined): string {
  return score == null ? "—" : formatDecimal(score);
}

/* ------------------------------------------------------------------ */
/* Small blocks shared by both report kinds                             */
/* ------------------------------------------------------------------ */

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
      <p className="text-xl font-bold tabular-nums text-slate-800 dark:text-slate-100 mt-0.5">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function TextList({ title, items, empty, tone }: { title: string; items: { text: string }[]; empty: string; tone: "good" | "bad" | "neutral" }) {
  const bullet = tone === "good" ? "bg-emerald-500" : tone === "bad" ? "bg-rose-500" : "bg-primary";
  return (
    <div className={CARD_PAD}>
      <SectionTitle>{title}</SectionTitle>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400 dark:text-slate-500">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item, i) => (
            <li key={i} className="flex gap-2 text-sm text-slate-700 dark:text-slate-200">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${bullet}`} />
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Monthly detail                                                       */
/* ------------------------------------------------------------------ */

function DoctorHighlight({ label, doctor, figure }: { label: string; doctor: ReportDoctor | null; figure: (d: ReportDoctor) => string }) {
  return (
    <div className="rounded-xl border border-slate-100 dark:border-slate-700 p-3">
      <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{label}</p>
      {doctor ? (
        <>
          <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mt-0.5 truncate" title={doctor.name}>
            {doctor.name}
          </p>
          <p className="text-xs tabular-nums text-slate-600 dark:text-slate-300">{figure(doctor)}</p>
        </>
      ) : (
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Not enough data</p>
      )}
    </div>
  );
}

function MonthlyDetail({ payload, generatedAt }: { payload: MonthlyReportPayload; generatedAt: string }) {
  const health = payload.health;
  const band = healthBand(health?.score);
  const bmd = payload.bookmydentist;
  return (
    <div className="space-y-4">
      <div className={CARD_PAD}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Monthly report</p>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">{payload.label}</h2>
        <Caption>
          Generated {generatedLabel(generatedAt)} · {formatNumber(payload.daysWithData)} days with data
        </Caption>
      </div>

      <div className={CARD_PAD}>
        <SectionTitle hint="Score at month end against the month before">Clinic Health Score</SectionTitle>
        {!health ? (
          <p className="text-sm text-slate-400 dark:text-slate-500">No score for this month yet</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="flex items-baseline gap-2">
                <span className={`text-5xl font-bold tabular-nums ${band ? BAND_TEXT[band] : "text-slate-800 dark:text-slate-100"}`}>{scoreText(health.score)}</span>
                <span className="text-sm text-slate-400 dark:text-slate-500">/ 100</span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 tabular-nums">
                Previous {scoreText(health.previous)} ·{" "}
                <span className={health.change == null ? "" : health.change > 0 ? CHANGE_TONE.good : health.change < 0 ? CHANGE_TONE.bad : CHANGE_TONE.neutral}>
                  {health.change == null ? "no previous" : `${health.change > 0 ? "+" : ""}${formatDecimal(health.change)} points`}
                </span>
              </p>
              <Caption>
                Based on {health.basedOn} of {health.total} areas
              </Caption>
            </div>
            <ul className="md:col-span-2 space-y-2">
              {health.areas.map((a) => (
                <li key={a.key} className="text-xs">
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <span className="text-slate-600 dark:text-slate-300">{a.label}</span>
                    <span className="tabular-nums">
                      <span className="font-semibold text-slate-800 dark:text-slate-100">{scoreText(a.score)}</span>
                      <span className="text-slate-400 dark:text-slate-500"> · prev {scoreText(a.previous)}</span>
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700/60 overflow-hidden">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${scoreWidth(a.score)}%` }} />
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-700/60 overflow-hidden">
                      <div className="h-full rounded-full bg-slate-300 dark:bg-slate-500" style={{ width: `${scoreWidth(a.previous)}%` }} />
                    </div>
                  </div>
                </li>
              ))}
              {health.inactive.length > 0 && (
                <li className="text-[11px] text-slate-400 dark:text-slate-500">Not scored: {health.inactive.map((a) => a.label).join(", ")}</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <TableCard title="Key figures" hint="This month against last month and the same month last year">
        <table className="w-full">
          <thead>
            <tr className={HEAD}>
              <th className={TH}>Figure</th>
              <th className={TH_NUM}>This month</th>
              <th className={TH_NUM}>Last month</th>
              <th className={TH_NUM}>Change</th>
              <th className={TH_NUM}>Last year</th>
              <th className={TH_NUM}>Change</th>
            </tr>
          </thead>
          <tbody className={BODY}>
            {KPI_ORDER.filter((k) => payload.kpis[k]).map((k) => {
              const row = payload.kpis[k];
              return (
                <tr key={k} className={TR}>
                  <td className={TD}>{row.label}</td>
                  <td className={`${TD_NUM} font-semibold`}>{formatUnit(row.current, row.unit)}</td>
                  <td className={TD_NUM}>{formatUnit(row.previous, row.unit)}</td>
                  <td className={`${TD_NUM} ${CHANGE_TONE[kpiRowTone(row)]}`}>{formatChange(row.change)}</td>
                  <td className={TD_NUM}>{formatUnit(row.lastYear, row.unit)}</td>
                  <td className={`${TD_NUM} ${CHANGE_TONE[kpiRowTone({ change: row.changeYear, higherIsBetter: row.higherIsBetter })]}`}>{formatChange(row.changeYear)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TextList title="Top wins" items={payload.wins} empty="No improvements stood out this month" tone="good" />
        <TextList title="Top concerns" items={payload.concerns} empty="Nothing slipped this month" tone="bad" />
      </div>

      <TableCard
        title="Doctors"
        hint="Revenue, patients and completion in this month"
      >
        <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <DoctorHighlight label="Top revenue" doctor={payload.doctors.topRevenue} figure={(d) => formatRs(d.revenue)} />
          <DoctorHighlight label="Best plan completion" doctor={payload.doctors.bestCompletion} figure={(d) => formatPct(d.completionRate)} />
          <DoctorHighlight label="Highest no-show rate" doctor={payload.doctors.highestNoShow} figure={(d) => formatPct(d.noShowRate)} />
        </div>
        <table className="w-full">
          <thead>
            <tr className={HEAD}>
              <th className={TH}>Doctor</th>
              <th className={TH_NUM}>Revenue</th>
              <th className={TH_NUM}>Patients</th>
              <th className={TH_NUM}>Appointments</th>
              <th className={TH_NUM}>No-show</th>
              <th className={TH_NUM}>Completion</th>
              <th className={TH_NUM}>Avg bill</th>
            </tr>
          </thead>
          <tbody className={BODY}>
            {payload.doctors.all.length === 0 ? (
              <EmptyRow colSpan={7}>No doctor activity this month</EmptyRow>
            ) : (
              payload.doctors.all.map((d) => (
                <tr key={d.doctorId} className={TR}>
                  <td className={TD}>{d.name}</td>
                  <td className={TD_NUM}>{formatRs(d.revenue)}</td>
                  <td className={TD_NUM}>{formatNumber(d.patients)}</td>
                  <td className={TD_NUM}>{formatNumber(d.appointments)}</td>
                  <td className={TD_NUM}>{formatPct(d.noShowRate)}</td>
                  <td className={TD_NUM}>{formatPct(d.completionRate)}</td>
                  <td className={TD_NUM}>{formatRs(d.averageBill)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </TableCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={CARD_PAD}>
          <SectionTitle hint="Online bookings and reviews in this month">BookMyDentist</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Stat label="Profile views" value={formatNumber(bmd.views)} />
            <Stat label="Started booking" value={formatNumber(bmd.bookingStarts)} />
            <Stat label="Bookings" value={formatNumber(bmd.bookings)} sub={`${formatNumber(bmd.paidOnline)} paid online · ${formatNumber(bmd.payAtClinic)} at clinic`} />
            <Stat label="Reviews" value={formatNumber(bmd.reviews)} />
            <Stat label="Average rating" value={formatStars(bmd.ratingAvg)} />
          </div>
        </div>
        <div className={CARD_PAD}>
          <SectionTitle hint="Snapshot at month end">Money owed, plans open, patients slipping</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat
              label="Unpaid"
              value={payload.unpaid ? formatRs(payload.unpaid.b0_30 + payload.unpaid.b31_60 + payload.unpaid.b60p) : "—"}
              sub={payload.unpaid ? `${formatRs(payload.unpaid.b60p)} over 60 days` : "No snapshot"}
            />
            <Stat
              label="Unfinished plans"
              value={payload.unfinished ? formatRs(payload.unfinished.value) : "—"}
              sub={payload.unfinished ? `${formatNumber(payload.unfinished.lines)} lines · ${formatNumber(payload.unfinished.patients)} patients` : "No snapshot"}
            />
            <Stat
              label="At-risk patients"
              value={payload.atRisk ? formatNumber(payload.atRisk.amber + payload.atRisk.red) : "—"}
              sub={payload.atRisk ? `${formatNumber(payload.atRisk.red)} red · ${formatNumber(payload.atRisk.amber)} amber` : "No snapshot"}
            />
          </div>
        </div>
      </div>

      <TextList title="3 things for next month" items={payload.nextMonth} empty="Keep doing what you are doing — no single area stands out" tone="neutral" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Daily detail                                                         */
/* ------------------------------------------------------------------ */

function DailyDetail({ payload, period, generatedAt }: { payload: DailyReportPayload; period: string; generatedAt: string }) {
  const money = payload.money;
  const appts = payload.appointments;
  const plan = payload.plan;
  const sources = appts ? Object.entries(appts.bySource).filter(([, v]) => typeof v === "number" && v > 0) : [];
  return (
    <div className="space-y-4">
      <div className={CARD_PAD}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">Daily report</p>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">{reportTitle("daily", payload.day || period)}</h2>
        <Caption>Generated {generatedLabel(generatedAt)}</Caption>
      </div>

      {payload.empty ? (
        <EmptyState title="Nothing recorded on this day">No receipts, appointments or plan changes were written for this day.</EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className={CARD_PAD}>
              <SectionTitle>Money</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Stat label="Billed" value={formatRs(money?.billed)} />
                <Stat label="Collected" value={formatRs(money?.collected)} sub={`${formatNumber(money?.receipts)} receipts`} />
                <Stat label="Discounts" value={formatRs(money?.discount)} />
                <Stat label="Cash" value={formatRs(money?.cash)} />
                <Stat label="Card" value={formatRs(money?.card)} />
                <Stat label="Bank / online" value={formatRs((money?.bankTransfer ?? 0) + (money?.online ?? 0))} sub={`Due created ${formatRs(money?.dueCreated)}`} />
              </div>
            </div>
            <div className={CARD_PAD}>
              <SectionTitle>Patients and appointments</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <Stat label="New patients" value={formatNumber(payload.patients?.new)} />
                <Stat label="Patients seen" value={formatNumber(payload.patients?.seen)} />
                <Stat label="Appointments" value={formatNumber(appts?.total)} />
                <Stat label="Completed" value={formatNumber(appts?.completed)} />
                <Stat label="No-shows" value={formatNumber(appts?.noShow)} />
                <Stat label="Cancelled" value={formatNumber(appts?.cancelled)} />
              </div>
              {sources.length > 0 && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
                  By source: {sources.map(([k, v]) => `${sourceLabel(k)} ${formatNumber(v as number)}`).join(" · ")}
                </p>
              )}
            </div>
          </div>

          <div className={CARD_PAD}>
            <SectionTitle>Treatment plans</SectionTitle>
            {!plan ? (
              <p className="text-xs text-slate-400 dark:text-slate-500">No plan activity</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Stat label="Lines planned" value={formatNumber(plan.linesCreated)} sub={formatRs(plan.valueCreated)} />
                <Stat label="Lines completed" value={formatNumber(plan.linesCompleted)} sub={formatRs(plan.valueCompleted)} />
                {payload.online && <Stat label="Online bookings" value={formatNumber(payload.online.bookings)} sub={`${formatNumber(payload.online.paidOnline)} paid online`} />}
                {payload.sms && <Stat label="SMS sent" value={formatNumber(payload.sms.sent)} sub={`${formatNumber(payload.sms.reminders)} reminders`} />}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TableCard title="Doctors">
              <table className="w-full">
                <thead>
                  <tr className={HEAD}>
                    <th className={TH}>Doctor</th>
                    <th className={TH_NUM}>Revenue</th>
                    <th className={TH_NUM}>Patients</th>
                    <th className={TH_NUM}>Appointments</th>
                    <th className={TH_NUM}>No-shows</th>
                  </tr>
                </thead>
                <tbody className={BODY}>
                  {!payload.doctors || payload.doctors.length === 0 ? (
                    <EmptyRow colSpan={5}>No doctor activity</EmptyRow>
                  ) : (
                    payload.doctors.map((d) => (
                      <tr key={d.doctorId} className={TR}>
                        <td className={TD}>{d.name}</td>
                        <td className={TD_NUM}>{formatRs(d.revenue)}</td>
                        <td className={TD_NUM}>{formatNumber(d.patients)}</td>
                        <td className={TD_NUM}>{formatNumber(d.appointments)}</td>
                        <td className={TD_NUM}>{formatNumber(d.noShow)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableCard>
            <TableCard title="Top treatments" hint="Lines added that day, by value">
              <table className="w-full">
                <thead>
                  <tr className={HEAD}>
                    <th className={TH}>Treatment</th>
                    <th className={TH_NUM}>Count</th>
                    <th className={TH_NUM}>Value</th>
                  </tr>
                </thead>
                <tbody className={BODY}>
                  {!payload.topTreatments || payload.topTreatments.length === 0 ? (
                    <EmptyRow colSpan={3}>No treatments planned</EmptyRow>
                  ) : (
                    payload.topTreatments.map((t) => (
                      <tr key={t.key} className={TR}>
                        <td className={TD}>{t.name}</td>
                        <td className={TD_NUM}>{formatNumber(t.count)}</td>
                        <td className={TD_NUM}>{formatRs(t.value)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </TableCard>
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Detail wrapper (fetch + print)                                       */
/* ------------------------------------------------------------------ */

function ReportDetail({ kind, period, onBack, onViewed }: { kind: ReportKind; period: string; onBack: () => void; onViewed: (period: string) => void }) {
  const { data, loading } = useAnalyticsFetch<MonthlyReportDetail | DailyReportDetail>(
    () => (kind === "monthly" ? getMonthlyReport(period) : getDailyReport(period)),
    [kind, period],
    "Could not load the report"
  );

  useEffect(() => {
    if (data) onViewed(period);
  }, [data, period, onViewed]);

  return (
    <div className="analytics-report space-y-4">
      <div className="analytics-no-print flex items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-primary dark:text-slate-400 dark:hover:text-primaryLight">
          <ArrowBackIcon sx={{ fontSize: 14 }} /> All {kind} reports
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          disabled={loading || !data}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-white text-xs font-medium shadow-sm hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <PrintIcon sx={{ fontSize: 16 }} /> Print / Save as PDF
        </button>
      </div>
      {loading || !data ? (
        <div className="grid grid-cols-1 gap-4">
          <CardSkeleton cards={3} height="h-32" />
        </div>
      ) : data.kind === "monthly" ? (
        <MonthlyDetail payload={data.payload} generatedAt={data.generatedAt} />
      ) : (
        <DailyDetail payload={data.payload} period={period} generatedAt={data.generatedAt} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* List                                                                 */
/* ------------------------------------------------------------------ */

function ReportList({ kind, items, viewedLocally, onOpen }: { kind: ReportKind; items: ReportListItem[]; viewedLocally: Set<string>; onOpen: (period: string) => void }) {
  return (
    <TableCard title={kind === "monthly" ? "Monthly reports" : "Daily reports"} hint={kind === "monthly" ? "Written on the 1st for the month just ended" : "Written each night for the day just ended · kept 90 days"}>
      <table className="w-full">
        <thead>
          <tr className={HEAD}>
            <th className={TH}>{kind === "monthly" ? "Month" : "Day"}</th>
            <th className={TH_NUM}>Health Score</th>
            <th className={TH_NUM}>Collected</th>
            <th className={TH_NUM}>Appointments</th>
            <th className={TH}>Generated</th>
          </tr>
        </thead>
        <tbody className={BODY}>
          {items.map((item) => {
            const isNew = reportIsNew(item) && !viewedLocally.has(item.period);
            return (
              <tr key={item.period} className={`${TR} cursor-pointer`} onClick={() => onOpen(item.period)}>
                <td className={TD}>
                  <span className="font-medium text-primary dark:text-primaryLight">{reportTitle(kind, item.period, item.label)}</span>
                  {isNew && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary dark:bg-primary/20 dark:text-primaryLight">New</span>
                  )}
                </td>
                <td className={TD_NUM}>
                  {item.score == null ? (
                    "—"
                  ) : (
                    <>
                      <span className="font-semibold">{formatDecimal(item.score)}</span>
                      {item.scoreChange != null && (
                        <span className={`ml-1.5 text-xs ${item.scoreChange > 0 ? CHANGE_TONE.good : item.scoreChange < 0 ? CHANGE_TONE.bad : CHANGE_TONE.neutral}`}>
                          {item.scoreChange > 0 ? "▲" : item.scoreChange < 0 ? "▼" : "•"} {formatDecimal(Math.abs(item.scoreChange))}
                        </span>
                      )}
                    </>
                  )}
                </td>
                <td className={TD_NUM}>{formatRs(item.collected)}</td>
                <td className={TD_NUM}>{formatNumber(item.appointments)}</td>
                <td className={TD}>{generatedLabel(item.generatedAt)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableCard>
  );
}

export default function AnalyticsReports() {
  const [kind, setKind] = useState<ReportKind>("monthly");
  const [selected, setSelected] = useState<string | null>(null);
  const [viewedLocally, setViewedLocally] = useState<Set<string>>(() => new Set());
  const { data, loading } = useAnalyticsFetch(() => getReports(kind), [kind], "Could not load reports");

  const items = data ? sortReportsNewest(data.list) : [];

  const markViewed = React.useCallback((period: string) => {
    setViewedLocally((prev) => {
      if (prev.has(period)) return prev;
      const next = new Set(prev);
      next.add(period);
      return next;
    });
  }, []);

  const switchKind = (next: ReportKind) => {
    if (next === kind) return;
    setKind(next);
    setSelected(null);
  };

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <div className="analytics-no-print rounded-2xl border bg-white border-slate-100 shadow-sm dark:bg-slate-800 dark:border-slate-700 px-4 py-3 flex items-center gap-3">
        <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Reports</span>
        <div className="flex items-center gap-1" role="tablist" aria-label="Report kind">
          {(["monthly", "daily"] as ReportKind[]).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={kind === k} className={`${TOGGLE_BTN} ${kind === k ? TOGGLE_ON : TOGGLE_OFF}`} onClick={() => switchKind(k)}>
              {k === "monthly" ? "Monthly" : "Daily"}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400 dark:text-slate-500 hidden sm:inline">
          {kind === "monthly" ? "One report per month, written on the 1st" : "One report per day, kept for 90 days"}
        </span>
      </div>

      {selected ? (
        <ReportDetail kind={kind} period={selected} onBack={() => setSelected(null)} onViewed={markViewed} />
      ) : loading || !data ? (
        <div className={`${CARD} overflow-hidden`}>
          <table className="w-full">
            <tbody>
              <TableSkeleton rows={5} colSpan={5} />
            </tbody>
          </table>
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="No reports yet">
          {kind === "monthly" ? "No reports yet — the first monthly report is written on the 1st of next month" : "No reports yet — the first daily report is written tonight"}
        </EmptyState>
      ) : (
        <ReportList kind={kind} items={items} viewedLocally={viewedLocally} onOpen={setSelected} />
      )}
    </div>
  );
}
